import { useEffect } from 'react';

const DOMAIN = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL)
  || 'https://litbuyspreadsheets.christmas';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

function jsonLdScript(data: Record<string, unknown>) {
  return JSON.stringify({ '@context': 'https://schema.org', ...data });
}

export function SEOJsonLd({ data }: { data: Record<string, unknown> }) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({ '@context': 'https://schema.org', ...data });
    script.id = `seo-ld-${data['@type'] || 'generic'}`;
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById(script.id);
      if (existing) existing.remove();
    };
  }, [data]);

  return null;
}

function makeBreadcrumb(items: BreadcrumbItem[]) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

function makeFAQ(faqs: FAQItem[]) {
  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
}

function makeOrganization() {
  return {
    '@type': 'Organization',
    name: 'LitBuy Spreadsheets',
    url: DOMAIN,
    logo: `${DOMAIN}/favicon.png`,
    sameAs: ['https://kaspreadsheet.com/'],
    description: 'Smart LitBuy Spreadsheet resources, guides, and data insights for modern shoppers.',
    foundingDate: '2025',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      url: DOMAIN,
    },
  };
}

export function HomeSEO() {
  const faqs: FAQItem[] = [
    {
      question: 'What is a LitBuy Spreadsheet?',
      answer: 'A LitBuy Spreadsheet is a purpose-built digital organizational framework that combines the familiar grid structure of traditional spreadsheet software with specialized features designed specifically for product research, price comparison, and purchase optimization across 15+ product categories including Shoes, Hoodies, T-Shirts, and more.',
    },
    {
      question: 'Why should I use LitBuy over a regular spreadsheet?',
      answer: 'Regular spreadsheets require manual setup and formula creation. LitBuy Spreadsheets provide ready-to-use templates tailored to specific shopping categories with built-in price comparison formulas, automated trend analysis, and collaborative sharing features — saving hours of setup time.',
    },
    {
      question: 'Is LitBuy Spreadsheet free to use?',
      answer: 'LitBuy Spreadsheets offers completely free educational resources, guides, and template information. You can access all our guides and articles without any registration or payment.',
    },
    {
      question: 'Which product categories does LitBuy support?',
      answer: 'LitBuy Spreadsheets cover 15 comprehensive product categories including Shoes, Hoodies/Sweaters, T-Shirts, Jackets, Pants/Shorts, Headwear, Sets, Underwear/Underpants, Jersey, Accessories, Bags, Belts, Perfumes, and OtherStuff.',
    },
    {
      question: 'How safe is my data when using LitBuy Spreadsheets?',
      answer: 'LitBuy Spreadsheets is an educational resource that does not collect, store, or process any personal financial data. All guides and templates are informational tools. We recommend users always verify data independently and use secure internet connections.',
    },
    {
      question: 'How do LitBuy Spreadsheets compare to traditional spreadsheets?',
      answer: 'LitBuy Spreadsheets offer pre-built category-specific templates, automated comparison engines, price tracking, interactive dashboards, and real-time collaboration — features that traditional spreadsheets require hours of manual setup to replicate.',
    },
  ];

  const breadcrumb: BreadcrumbItem[] = [{ name: 'Home', url: DOMAIN }];

  return (
    <>
      <SEOJsonLd data={makeOrganization()} />
      <SEOJsonLd data={{
        '@type': 'WebSite',
        name: 'LitBuy Spreadsheets',
        url: DOMAIN,
        description: 'Comprehensive LitBuy Spreadsheet guides, templates, and resources for smart shoppers.',
        inLanguage: 'en-US',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${DOMAIN}/search?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      }} />
      <SEOJsonLd data={makeBreadcrumb(breadcrumb)} />
      <SEOJsonLd data={makeFAQ(faqs)} />
    </>
  );
}

export function ArticleSEO({
  title,
  description,
  slug,
  datePublished,
  dateModified,
  imageUrl,
  faqs,
  category,
}: {
  title: string;
  description: string;
  slug: string;
  datePublished?: string;
  dateModified?: string;
  imageUrl?: string;
  faqs: FAQItem[];
  category?: string;
}) {
  const articleUrl = `${DOMAIN}${slug}`;
  const pubDate = datePublished || '2026-06-01';
  const modDate = dateModified || '2026-06-20';

  const breadcrumb: BreadcrumbItem[] = [
    { name: 'Home', url: DOMAIN },
    { name: 'Shopping Guide', url: `${DOMAIN}/shopping-guide` },
    { name: title, url: articleUrl },
  ];

  return (
    <>
      <SEOJsonLd data={makeBreadcrumb(breadcrumb)} />
      <SEOJsonLd data={{
        '@type': 'Article',
        headline: title,
        description,
        url: articleUrl,
        datePublished: pubDate,
        dateModified: modDate,
        author: {
          '@type': 'Organization',
          name: 'LitBuy Editorial Team',
          url: DOMAIN,
        },
        publisher: {
          '@type': 'Organization',
          name: 'LitBuy Spreadsheets',
          url: DOMAIN,
          logo: { '@type': 'ImageObject', url: `${DOMAIN}/favicon.png` },
        },
        image: imageUrl ? { '@type': 'ImageObject', url: imageUrl } : undefined,
        inLanguage: 'en-US',
        mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
        ...(category ? { articleSection: category } : {}),
      }} />
      <SEOJsonLd data={makeFAQ(faqs)} />
    </>
  );
}

export function CollectionPageSEO({
  title,
  description,
  slug,
}: {
  title: string;
  description: string;
  slug: string;
}) {
  const pageUrl = `${DOMAIN}${slug}`;

  const breadcrumb: BreadcrumbItem[] = [
    { name: 'Home', url: DOMAIN },
    { name: title, url: pageUrl },
  ];

  return (
    <>
      <SEOJsonLd data={makeBreadcrumb(breadcrumb)} />
      <SEOJsonLd data={{
        '@type': 'CollectionPage',
        name: title,
        description,
        url: pageUrl,
        inLanguage: 'en-US',
        isPartOf: { '@type': 'WebSite', name: 'LitBuy Spreadsheets', url: DOMAIN },
      }} />
    </>
  );
}